/**
 * Shared utility for safely updating college data with AI
 * Includes rate limiting, undo capability, and field protection
 */

import { supabase } from '../lib/supabase';
import { flattenCollegeData } from './collegeDataFlattener';
import toast from 'react-hot-toast';

// Track if an update is currently in progress (one at a time)
let isUpdating = false;

// Store update history for undo capability (in memory for session)
const updateHistory = new Map();

// Default locked fields that should never be overwritten by AI
const DEFAULT_LOCKED_FIELDS = [
  'student_advantages',
  'application_strategy',
  'notes',
  'strategic_classification',
  'recommended_strategy',
  'custom_notes'
];

// Helper function to determine current application year
function getCurrentApplicationYear() {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  
  // If it's September or later, we're in the next year's application cycle
  if (currentMonth >= 9) {
    return `${currentYear}-${currentYear + 1}`;
  } else {
    return `${currentYear - 1}-${currentYear}`;
  }
}

// Transform AI essay requirements response to our JSONB format
const transformAIEssayRequirements = (aiEssayData) => {
  if (!aiEssayData) return null;
  
  const transformed = {
    platform: 'common_app', // Default, could be detected from AI response
    personalStatementRequired: true, // Default for most schools
    essays: [],
    additionalInfo: '',
    // Include special_requirements for backwards compatibility
    special_requirements: {
      video_essay: false,
      portfolio: false,
      writing_sample: false
    },
    // Include verification fields
    prompt_year: getCurrentApplicationYear(),
    verification_status: 'ai_fetched',
    confidence_score: 75,
    last_verified_date: new Date().toISOString()
  };
  
  // Process number of supplemental essays
  if (aiEssayData.number_of_supplemental_essays) {
    const count = parseInt(aiEssayData.number_of_supplemental_essays);
    console.log(`📝 Found ${count} supplemental essays`);
  }
  
  // Process essay prompts and word limits
  // Check multiple possible field names from AI response or flattened data
  const promptData = aiEssayData.essay_prompts_and_word_limits || 
                    aiEssayData.essay_prompts || 
                    aiEssayData.prompts;
  
  if (promptData) {
    const prompts = promptData;
    
    if (Array.isArray(prompts)) {
      transformed.essays = prompts.map((prompt) => {
        if (typeof prompt === 'object' && prompt.prompt) {
          const wordLimit = prompt.word_limit || prompt.wordLimit || 250;
          return {
            type: detectEssayType(prompt.prompt),
            prompt: prompt.prompt,
            wordLimit: wordLimit,
            word_limit: wordLimit, // Include both field names for compatibility
            required: true,
            notes: prompt.notes || ''
          };
        } else if (typeof prompt === 'string') {
          // Parse string format like "1. Why Us? (250 words)"
          const wordLimitMatch = prompt.match(/\((\d+)\s*words?\)/i);
          const wordLimit = wordLimitMatch ? parseInt(wordLimitMatch[1]) : 250;
          const cleanPrompt = prompt.replace(/^\d+\.\s*/, '').replace(/\(\d+\s*words?\)/i, '').trim();
          
          return {
            type: detectEssayType(cleanPrompt),
            prompt: cleanPrompt,
            wordLimit: wordLimit,
            word_limit: wordLimit, // Include both field names for compatibility
            required: true,
            notes: ''
          };
        }
        return null;
      }).filter(Boolean);
    } else if (typeof prompts === 'string') {
      // Handle single prompt or multi-line string
      const lines = prompts.split('\n').filter(line => line.trim());
      transformed.essays = lines.map(line => {
        const wordLimitMatch = line.match(/\((\d+)\s*words?\)/i);
        const wordLimit = wordLimitMatch ? parseInt(wordLimitMatch[1]) : 250;
        const cleanPrompt = line.replace(/^\d+\.\s*/, '').replace(/\(\d+\s*words?\)/i, '').trim();
        
        return {
          type: detectEssayType(cleanPrompt),
          prompt: cleanPrompt,
          wordLimit: wordLimit,
          word_limit: wordLimit, // Include both field names for compatibility
          required: true,
          notes: ''
        };
      });
    }
  }
  
  // Process "Why Us?" essay specifics
  if (aiEssayData.why_us_essay_specifics || aiEssayData.why_essay) {
    const whyUsInfo = aiEssayData.why_us_essay_specifics || aiEssayData.why_essay;
    transformed.additionalInfo = typeof whyUsInfo === 'string' ? whyUsInfo : '';
    
    // Add Why Us essay if not already in essays array
    const hasWhyUs = transformed.essays.some(e => e.type === 'why_us');
    if (!hasWhyUs && whyUsInfo) {
      transformed.essays.unshift({
        type: 'why_us',
        prompt: 'Why are you interested in our institution?',
        wordLimit: 250,
        word_limit: 250, // Include both field names for compatibility
        required: true,
        notes: whyUsInfo
      });
    }
  }
  
  return transformed;
};

// Helper to detect essay type from prompt text
const detectEssayType = (prompt) => {
  const lowerPrompt = prompt.toLowerCase();
  
  if (lowerPrompt.includes('why us') || lowerPrompt.includes('why our') || 
      lowerPrompt.includes('why do you want') || lowerPrompt.includes('interest in')) {
    return 'why_us';
  } else if (lowerPrompt.includes('activity') || lowerPrompt.includes('extracurricular')) {
    return 'activity';
  } else if (lowerPrompt.includes('community') || lowerPrompt.includes('contribute')) {
    return 'community';
  } else if (lowerPrompt.includes('academic') || lowerPrompt.includes('intellectual')) {
    return 'academic_interest';
  } else if (lowerPrompt.includes('challenge') || lowerPrompt.includes('overcome')) {
    return 'challenge';
  } else if (lowerPrompt.includes('diversity') || lowerPrompt.includes('background')) {
    return 'diversity';
  } else if (lowerPrompt.includes('leadership')) {
    return 'leadership';
  } else {
    return 'supplemental';
  }
};

/**
 * Get locked fields for a specific college from localStorage
 */
export function getLockedFields(collegeId) {
  try {
    const stored = localStorage.getItem(`locked_fields_${collegeId}`);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error reading locked fields:', error);
  }
  return [...DEFAULT_LOCKED_FIELDS];
}

/**
 * Set locked fields for a specific college
 */
export function setLockedFields(collegeId, fields) {
  try {
    localStorage.setItem(`locked_fields_${collegeId}`, JSON.stringify(fields));
  } catch (error) {
    console.error('Error saving locked fields:', error);
  }
}

/**
 * Toggle lock status for a specific field
 */
export function toggleFieldLock(collegeId, fieldName) {
  const currentLocked = getLockedFields(collegeId);
  const isLocked = currentLocked.includes(fieldName);
  
  if (isLocked) {
    setLockedFields(collegeId, currentLocked.filter(f => f !== fieldName));
  } else {
    setLockedFields(collegeId, [...currentLocked, fieldName]);
  }
  
  return !isLocked;
}

/**
 * Store update history for undo capability
 */
function storeUpdateHistory(collegeId, previousData, updatedFields) {
  const historyEntry = {
    timestamp: new Date().toISOString(),
    previousData,
    updatedFields,
    expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000) // 24 hours
  };
  
  updateHistory.set(collegeId, historyEntry);
  
  // Also store in localStorage for persistence
  try {
    const historyArray = Array.from(updateHistory.entries());
    localStorage.setItem('ai_update_history', JSON.stringify(historyArray));
  } catch (error) {
    console.error('Error storing update history:', error);
  }
}

/**
 * Load update history from localStorage on init
 */
export function loadUpdateHistory() {
  try {
    const stored = localStorage.getItem('ai_update_history');
    if (stored) {
      const historyArray = JSON.parse(stored);
      historyArray.forEach(([collegeId, entry]) => {
        // Only restore if not expired
        if (new Date(entry.expiresAt) > new Date()) {
          updateHistory.set(collegeId, entry);
        }
      });
    }
  } catch (error) {
    console.error('Error loading update history:', error);
  }
}

/**
 * Get update history for a college (for undo)
 */
export function getUpdateHistory(collegeId) {
  const history = updateHistory.get(collegeId);
  if (history && new Date(history.expiresAt) > new Date()) {
    return history;
  }
  return null;
}

/**
 * Main function to fetch and update college data with AI
 * Enforces one-at-a-time updates and respects locked fields
 */
export async function updateCollegeWithAI(college, options = {}) {
  const {
    onProgress = () => {},
    onSuccess = () => {},
    onError = () => {},
    respectLocks = true
  } = options;
  
  // Enforce one-at-a-time updates
  if (isUpdating) {
    toast.error('Another update is in progress. Please wait.');
    return { success: false, error: 'Update already in progress' };
  }
  
  isUpdating = true;
  onProgress(true);
  
  try {
    // Determine the correct global college ID (for student colleges vs global colleges)
    const globalCollegeId = college.global_college_id || college.id;
    
    // Clean college name - remove any suffixes like "Political Science" or after dash
    let cleanCollegeName = college.name;
    // Remove text after dash or parentheses
    if (cleanCollegeName.includes(' - ')) {
      cleanCollegeName = cleanCollegeName.split(' - ')[0].trim();
    }
    if (cleanCollegeName.includes(' (')) {
      cleanCollegeName = cleanCollegeName.split(' (')[0].trim();
    }
    
    console.log('🔄 Fetching latest data for:', cleanCollegeName, '(ID:', globalCollegeId, ')');
    
    // Call the AI API
    const response = await fetch('/api/anthropic/suggestions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'fetch-college-data',
        collegeName: cleanCollegeName,
        includeRankings: true
      })
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'No data returned from AI service');
    }
    
    console.log('📦 Raw AI data received:', result.data);
    
    // Flatten the nested AI response
    const flattenedData = flattenCollegeData(result.data, { verbose: true });
    
    // Get locked fields for this college (use global ID for consistency)
    const lockedFields = respectLocks ? getLockedFields(globalCollegeId) : [];
    
    // Store current data for undo before updating
    const { data: currentData, error: fetchError } = await supabase
      .from('global_colleges')
      .select('*')
      .eq('id', globalCollegeId)
      .single();
    
    if (fetchError) {
      throw new Error('Failed to fetch current data: ' + fetchError.message);
    }
    
    // Filter out locked fields from the update
    const updateData = { ...flattenedData };
    lockedFields.forEach(field => {
      if (field in updateData) {
        delete updateData[field];
        console.log(`🔒 Skipping locked field: ${field}`);
      }
    });
    
    // Add metadata
    updateData.data_last_updated = new Date().toISOString();
    updateData.ai_update_timestamp = new Date().toISOString();
    
    // Track which fields we're actually updating
    const updatedFields = Object.keys(updateData).filter(key => 
      updateData[key] !== currentData[key]
    );
    
    // Store history for undo (use global ID)
    storeUpdateHistory(globalCollegeId, currentData, updatedFields);
    
    // Update the database
    const { error: updateError } = await supabase
      .from('global_colleges')
      .update(updateData)
      .eq('id', globalCollegeId);
    
    if (updateError) {
      throw new Error('Database update failed: ' + updateError.message);
    }
    
    // Mark fields as AI-updated in localStorage (use global ID)
    try {
      const aiUpdatedFields = JSON.parse(localStorage.getItem(`ai_updated_fields_${globalCollegeId}`) || '{}');
      updatedFields.forEach(field => {
        aiUpdatedFields[field] = new Date().toISOString();
      });
      localStorage.setItem(`ai_updated_fields_${globalCollegeId}`, JSON.stringify(aiUpdatedFields));
    } catch (error) {
      console.error('Error storing AI update metadata:', error);
    }
    
    // Process and save essay requirements to college_journey_colleges if available
    if (result.data.essay_requirements && college.id) {
      console.log('📝 Processing essay requirements from AI update...');
      
      // Transform AI essay data to our JSONB format
      const essayRequirements = transformAIEssayRequirements(result.data.essay_requirements);
      
      if (essayRequirements) {
        // Update the college_journey_colleges table with essay requirements
        const { error: essayError } = await supabase
          .from('college_journey_colleges')
          .update({ 
            essay_requirements: essayRequirements,
            updated_at: new Date().toISOString()
          })
          .eq('id', college.id);
          
        if (essayError) {
          console.error('Error updating essay requirements:', essayError);
        } else {
          console.log('✅ Essay requirements saved successfully');
          toast.success('Essay requirements updated');
        }
      }
    }
    
    const successMessage = `✅ Updated ${college.name} with latest ${result.dataYear || ''} data (${updatedFields.length} fields)`;
    toast.success(successMessage);
    onSuccess({ updatedFields, dataYear: result.dataYear });
    
    return { 
      success: true, 
      updatedFields, 
      dataYear: result.dataYear,
      lockedFields: lockedFields.length 
    };
    
  } catch (error) {
    console.error('Error updating college with AI:', error);
    const errorMessage = error.message || 'Failed to update college data';
    toast.error(errorMessage);
    onError(error);
    
    return { success: false, error: errorMessage };
    
  } finally {
    isUpdating = false;
    onProgress(false);
  }
}

/**
 * Undo the last AI update for a college
 */
export async function undoAIUpdate(collegeId, options = {}) {
  const {
    onSuccess = () => {},
    onError = () => {}
  } = options;
  
  const history = getUpdateHistory(collegeId);
  
  if (!history) {
    toast.error('No update history available for undo');
    return { success: false, error: 'No history available' };
  }
  
  try {
    // Restore only the fields that were updated
    const restoreData = {};
    history.updatedFields.forEach(field => {
      if (field in history.previousData) {
        restoreData[field] = history.previousData[field];
      }
    });
    
    // Update the database
    const { error } = await supabase
      .from('global_colleges')
      .update(restoreData)
      .eq('id', collegeId);
    
    if (error) {
      throw new Error('Failed to restore data: ' + error.message);
    }
    
    // Clear the history for this college
    updateHistory.delete(collegeId);
    
    // Clear AI update metadata
    try {
      localStorage.removeItem(`ai_updated_fields_${collegeId}`);
    } catch (error) {
      console.error('Error clearing AI metadata:', error);
    }
    
    toast.success('✅ Successfully restored previous data');
    onSuccess();
    
    return { success: true };
    
  } catch (error) {
    console.error('Error undoing AI update:', error);
    toast.error('Failed to undo update');
    onError(error);
    
    return { success: false, error: error.message };
  }
}

/**
 * Check if a field was updated by AI
 */
export function isFieldAIUpdated(collegeId, fieldName) {
  try {
    const aiUpdatedFields = JSON.parse(localStorage.getItem(`ai_updated_fields_${collegeId}`) || '{}');
    return fieldName in aiUpdatedFields;
  } catch (error) {
    return false;
  }
}

/**
 * Get AI update timestamp for a field
 */
export function getFieldAIUpdateTime(collegeId, fieldName) {
  try {
    const aiUpdatedFields = JSON.parse(localStorage.getItem(`ai_updated_fields_${collegeId}`) || '{}');
    return aiUpdatedFields[fieldName] || null;
  } catch (error) {
    return null;
  }
}

/**
 * Check if any update is currently in progress
 */
export function isAnyUpdateInProgress() {
  return isUpdating;
}

/**
 * Fetch essay prompts using the dedicated endpoint
 */
export async function fetchEssayPrompts(collegeName, year = null) {
  // Determine the current application year if not provided
  if (!year) {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    
    // If it's September or later, we're in the next year's application cycle
    if (currentMonth >= 9) {
      year = `${currentYear}-${currentYear + 1}`;
    } else {
      year = `${currentYear - 1}-${currentYear}`;
    }
  }
  
  console.log(`📝 Fetching essay prompts for ${collegeName} (${year} cycle)`);
  
  try {
    const response = await fetch('/api/anthropic/suggestions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'fetch-essay-prompts',
        collegeName,
        year
      })
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'No essay prompt data returned');
    }
    
    console.log('✅ Essay prompts fetched successfully');
    console.log('📊 Verification status:', result.data.verification_status);
    console.log('📊 Confidence score:', result.data.confidence_score);
    
    return result.data;
  } catch (error) {
    console.error('Error fetching essay prompts:', error);
    return null;
  }
}

/**
 * Update college with AI data including verified essay prompts
 */
export async function updateCollegeWithAIAndEssays(college, options = {}) {
  const {
    onProgress = () => {},
    onSuccess = () => {},
    onError = () => {},
    respectLocks = true,
    includeEssayPrompts = true
  } = options;
  
  // First, update general college data
  const dataResult = await updateCollegeWithAI(college, {
    ...options,
    includeEssayPrompts: false // We'll handle essays separately
  });
  
  if (!dataResult.success) {
    return dataResult;
  }
  
  // Then fetch essay prompts if requested
  if (includeEssayPrompts) {
    onProgress(true);
    
    try {
      const cleanCollegeName = college.name.split(' - ')[0].trim();
      const essayData = await fetchEssayPrompts(cleanCollegeName);
      
      if (essayData) {
        // Save essay requirements to college_journey_colleges
        const { error } = await supabase
          .from('college_journey_colleges')
          .update({ 
            essay_requirements: essayData,
            updated_at: new Date().toISOString()
          })
          .eq('id', college.id);
          
        if (error) {
          console.error('Error updating essay requirements:', error);
          toast.error('Failed to save essay requirements');
        } else {
          console.log('✅ Essay requirements saved with verification data');
          
          // Show appropriate message based on verification status
          if (essayData.verification_status === 'ai_fetched') {
            toast.info('Essay prompts fetched - please verify accuracy');
          } else if (essayData.verification_status === 'unverified') {
            toast.warning('Essay prompts may be outdated - verification needed');
          } else {
            toast.success('Essay requirements updated');
          }
        }
      }
    } catch (error) {
      console.error('Error fetching essay prompts:', error);
      // Don't fail the whole update if just essays fail
      toast.warning('Could not fetch essay prompts');
    } finally {
      onProgress(false);
    }
  }
  
  return dataResult;
}

// Load history on module init
loadUpdateHistory();